import { inject } from '@angular/core'
import { NavigationExtras } from '@angular/router'
import { _omit } from '@naturalcycles/js-lib'
import { WebSignupQueryParam } from '@naturalcycles/shared'
import { MixpanelService } from '@src/app/core/services/analytics/mixpanel.service.js'
import { PageFlowService, Path } from '@src/app/core/services/flow.service.js'
import { NavService } from '@src/app/core/services/nav.service.js'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { hasParentPage } from '@src/app/core/util/nav.util.js'
import { ParentPage } from '@src/app/shared/typings/enum/pages.js'
import {
  defaultCbQueryParams,
  defaultQueryParams,
} from '@src/app/shared/typings/enum/query-params.js'

type ExtrasWithCustomDeviceParams = NavigationExtras & {
  customDeviceParams?: WebSignupQueryParam[]
}

/**
 * A class to handle navigation for pages made to be part of a flow, to be
 * used in a composition pattern.
 */
export class FlowPageNavigator {
  constructor(private page: Path) {}
  protected flowService = inject(PageFlowService)
  protected navService = inject(NavService)
  protected store = inject(SignalStore)
  protected mixpanel = inject(MixpanelService)

  private parseExtras(extras?: ExtrasWithCustomDeviceParams): NavigationExtras {
    const isClearblue = hasParentPage(ParentPage.clearblue, this.navService.getCurrentUrl())
    const paramsToForward = extras?.customDeviceParams || [
      ...defaultQueryParams,
      ...(isClearblue ? defaultCbQueryParams : []),
    ]
    const deviceParams: { [key: string]: string | null } = {}
    paramsToForward.forEach(param => {
      deviceParams[param] = this.navService.getQueryParam(param)
    })

    if (extras) {
      extras.queryParams = {
        ...deviceParams,
        ...extras.queryParams,
      }
    }
    return extras ? _omit(extras, ['customDeviceParams']) : {}
  }

  private async navigate(path: Path, extras?: ExtrasWithCustomDeviceParams): Promise<boolean> {
    extras = this.parseExtras(extras)
    return await this.navService.goToPage(
      typeof path === 'string' ? [path] : [...path.pages],
      extras,
    )
  }

  /**
   * Move to the next page in the flow.
   */
  async next(extras?: ExtrasWithCustomDeviceParams): Promise<boolean> {
    const queryParams = { ...this.navService.getQueryParams(), ...extras?.queryParams }
    this.flowService.processConditions(queryParams)
    const nextPage = this.flowService.getNextPage(this.page, queryParams)
    return await this.navigate(nextPage, extras)
  }

  /**
   * Move to the previous page in the flow.
   */
  async previous(extras?: ExtrasWithCustomDeviceParams): Promise<boolean> {
    const queryParams = { ...this.navService.getQueryParams(), ...extras?.queryParams }
    this.flowService.processConditions(queryParams)
    const prevPage = this.flowService.getPreviousPage(this.page)
    return await this.navigate(prevPage, extras)
  }
}

/**
 * A page part of a flow described by the `PageFlowService`.
 */
export interface FlowPage {
  navigator: FlowPageNavigator
}
